import * as React from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Grid,
  LinearProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  getUnprocessedItem,
  resendUnprocessedItem,
} from "../redux/dynamo_db/actions";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../utils";
import getUserProfiles from "../tokenUtils";
import { MaterialSymbol } from "@datalake/towercontrol-banner";

// Detail page for a single row parked in data-pipelines' UnprocessedItems
// table. Unlike the list page's in-memory rows, this always re-fetches from
// the server (GET_UNPROCESSED_ITEM) since users can deep-link straight here
// and the item's state may have changed (e.g. already resent and deleted).
export default function UnprocessedItemDetails() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // Route: /unprocesseditems/details/<entity_type>/<unique_id>
  const pathParts = location.pathname.split("/");
  const uniqueId = decodeURIComponent(pathParts[pathParts.length - 1]);
  const entityType = pathParts[pathParts.length - 2];

  const [item, setItem] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editedJson, setEditedJson] = React.useState("");
  const [editorError, setEditorError] = React.useState(null);
  const [resending, setResending] = React.useState(false);
  const [resendSuccess, setResendSuccess] = React.useState(null);

  const profiles = getUserProfiles() ?? [];
  const isAdmin = profiles.some((p) => p.includes("admin"));

  const fetchItem = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    const acted = await dispatch(getUnprocessedItem(uniqueId, entityType));
    setLoading(false);
    if (acted?.errors) {
      setError(
        acted.errors.responseCode === 404
          ? "Item not found (it may already have been resolved)."
          : acted.errors.error ||
              `Request failed (${acted.errors.responseCode}).`,
      );
      setItem(null);
      return;
    }
    setItem(acted?.unprocessedItem || null);
  }, [dispatch, uniqueId, entityType]);

  React.useEffect(() => {
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueId, entityType]);

  const parsedRawData = React.useMemo(() => {
    if (!item?.raw_data) return null;
    try {
      return JSON.parse(item.raw_data);
    } catch {
      return null;
    }
  }, [item]);

  const openEditor = () => {
    setEditedJson(JSON.stringify(parsedRawData ?? {}, null, 2));
    setEditorError(null);
    setResendSuccess(null);
    setEditorOpen(true);
  };

  const handleExport = (format) => {
    if (!parsedRawData) return;
    const fields = Object.keys(parsedRawData).map((key) => ({ name: key }));
    const fileName = `${entityType}_${uniqueId}_reimport`;
    if (format === "csv") {
      downloadCSV(fields, fileName, ",", [parsedRawData]);
    } else {
      downloadJSON(fields, fileName, [parsedRawData]);
    }
  };

  const handleResend = async () => {
    let editedData;
    try {
      editedData = JSON.parse(editedJson);
    } catch (e) {
      setEditorError("Invalid JSON.");
      return;
    }
    setResending(true);
    setEditorError(null);
    let username = "unknown";
    try {
      username = extractUserInfoFromToken(getLocalStorageSAMLToken()).username;
    } catch (e) {
      // extractUserInfoFromToken throws if no valid token is found; keep going
      // with a placeholder rather than blocking the resend on it.
    }
    const acted = await dispatch(
      resendUnprocessedItem({
        uniqueId,
        entityType,
        editedData,
        requestUser: username,
      }),
    );
    setResending(false);
    if (acted?.errors) {
      setEditorError(
        acted.errors.error || `Resend failed (${acted.errors.responseCode}).`,
      );
      return;
    }
    setEditorOpen(false);
    setResendSuccess(
      "Resend request queued. The item will disappear from this list once data-pipelines confirms it was reprocessed.",
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate("/unprocesseditems")}>
          Back to list
        </Button>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">Item not found.</Alert>
      </Box>
    );
  }

  const displayFields = [
    { name: "Entity Type", value: item.SK },
    { name: "Unique ID", value: item.PK },
    { name: "Error Reason", value: item.error_reason },
    { name: "Error Message", value: item.error_message },
    { name: "Source", value: item.source },
    { name: "Batch ID", value: item.batch_id },
    { name: "Destination Queue", value: item.destination_queue },
    { name: "Created At", value: item.created_at },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, color: "var(--ale-primary)" }}>
        Unprocessed Item — {entityType} / {uniqueId}
      </Typography>

      {resendSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {resendSuccess}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          {displayFields.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.name}>
              <Typography variant="caption" color="text.secondary">
                {field.name}
              </Typography>
              <Typography variant="body2">{field.value ?? "—"}</Typography>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Button
          startIcon={<MaterialSymbol name="download" />}
          disabled={!parsedRawData}
          onClick={() => handleExport("csv")}
        >
          Export reimportable (CSV)
        </Button>
        <Button
          startIcon={<MaterialSymbol name="download" />}
          disabled={!parsedRawData}
          onClick={() => handleExport("json")}
        >
          Export reimportable (JSON)
        </Button>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<MaterialSymbol name="send" />}
            onClick={openEditor}
          >
            Resend
          </Button>
        )}
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Raw data
      </Typography>
      <Box
        component="pre"
        sx={{
          p: 2,
          bgcolor: "grey.100",
          overflowX: "auto",
          fontSize: "0.8rem",
        }}
      >
        {JSON.stringify(parsedRawData ?? item.raw_data, null, 2)}
      </Box>

      {editorOpen && (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Edit and resend
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Edit the JSON below (pre-filled with the parsed raw data) and
            resend. data-pipelines merges the edited fields into the original
            row and republishes it onto its original input queue.
          </Typography>
          <TextField
            multiline
            fullWidth
            minRows={10}
            value={editedJson}
            onChange={(e) => setEditedJson(e.target.value)}
            InputProps={{
              style: { fontFamily: "monospace", fontSize: "0.8rem" },
            }}
            sx={{ mb: 2 }}
          />
          {editorError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editorError}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<MaterialSymbol name="send" />}
              disabled={resending}
              onClick={handleResend}
            >
              Confirm resend
            </Button>
            <Button onClick={() => setEditorOpen(false)} disabled={resending}>
              Cancel
            </Button>
          </Box>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <Button onClick={() => navigate("/unprocesseditems")}>
          Back to list
        </Button>
      </Box>
    </Box>
  );
}
